import React, { useEffect, useId, useState } from 'react';
import { MDBBtn, MDBIcon, MDBInput, MDBInputGroup, MDBListGroup, MDBListGroupItem, MDBSpinner } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import useDebounce from '../../../utils/debounce';
import { captureError } from '../../../utils/sentryHelpers';

const COMPANY_IMAGE_BASE = 'https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/';

/**
 * Optional link of the item to a Company record: a chip when linked, otherwise
 * a debounced (300 ms, min. 2 characters) search. The search logic is the one
 * of the previous page, including the spinner reset when a search is cancelled.
 * @param {{companyId: string, linkedCompany: {id: string, name: string, missing?: boolean}|null, selectCompany: (company: object) => void, clearCompany: () => void, searchCompanies: (query: string) => Promise<object[]>, disabled?: boolean}} props
 */
export default function LinkedCompanyField({ companyId, linkedCompany, selectCompany, clearCompany, searchCompanies, disabled = false }) {
  const { t } = useTranslation();
  const uid = useId();
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
  const debouncedCompanyQuery = useDebounce(companyQuery, 300);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (debouncedCompanyQuery.trim().length < 2) {
        setCompanyResults([]);
        // A search cancelled by this change never clears the flag itself (see finally)
        setIsSearchingCompanies(false);
        return;
      }
      setIsSearchingCompanies(true);
      try {
        const results = await searchCompanies(debouncedCompanyQuery);
        if (!cancelled) setCompanyResults(results);
      } catch (error) {
        console.error('Error searching companies:', error);
        captureError(error, {
          feature: 'mediathek',
          operation: 'search-companies',
          context: { query: debouncedCompanyQuery },
          tags: { component: 'EditMediaItem' },
        });
        if (!cancelled) setCompanyResults([]);
      } finally {
        if (!cancelled) setIsSearchingCompanies(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedCompanyQuery, searchCompanies]);

  const labelId = `${uid}-label`;
  const helpId = `${uid}-help`;

  return (
    <div role="group" aria-labelledby={labelId}>
      <div className="me-slot-label mb-2" id={labelId}>{t('Linked company (optional)')}</div>
      {companyId ? (
        <div className="me-slot">
          <div className="me-slot-row">
            <span className="me-slot-icon" aria-hidden="true">
              <MDBIcon fas icon="building" />
            </span>
            <div className="me-slot-body">
              <span className="me-slot-label text-break">{linkedCompany?.name || companyId}</span>
              {linkedCompany?.missing && <span className="me-error-text d-block mt-0">{t('Company not found')}</span>}
            </div>
            <div className="me-slot-actions">
              <MDBBtn
                color="link"
                size="sm"
                type="button"
                className="me-icon-btn"
                onClick={clearCompany}
                disabled={disabled}
                aria-label={t('Remove link')}
                title={t('Remove link')}
              >
                <MDBIcon fas icon="times" aria-hidden="true" />
              </MDBBtn>
            </div>
          </div>
        </div>
      ) : (
        <div className="position-relative" style={{ maxWidth: '480px' }}>
          <MDBInputGroup>
            <MDBInput
              id={`${uid}-search`}
              label={t('Search Company')}
              value={companyQuery}
              disabled={disabled}
              autoComplete="off"
              aria-describedby={helpId}
              onChange={(e) => setCompanyQuery(e.target.value)}
            />
            {isSearchingCompanies && (
              <div className="input-group-text">
                <MDBSpinner size="sm" role="presentation" />
              </div>
            )}
          </MDBInputGroup>
          {companyResults.length > 0 && (
            <MDBListGroup tag="div" className="w-100 shadow-5 position-absolute bg-body" style={{ zIndex: 10 }}>
              {companyResults.map((company) => (
                <MDBListGroupItem
                  key={company.id}
                  tag="button"
                  type="button"
                  action
                  className="d-flex align-items-center text-start"
                  onClick={() => {
                    selectCompany(company);
                    setCompanyQuery('');
                    setCompanyResults([]);
                  }}
                >
                  {company.image?.main?.fileName ? (
                    <img
                      src={`${COMPANY_IMAGE_BASE}${company.image.main.fileName}`}
                      alt=""
                      className="me-3"
                      style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                    />
                  ) : (
                    <MDBIcon fas icon="building" className="me-3" aria-hidden="true" />
                  )}
                  <span>{company.name}</span>
                </MDBListGroupItem>
              ))}
            </MDBListGroup>
          )}
          <div className="me-help" id={helpId}>
            {t('Links the item to a company profile. The company name above stays the displayed fallback.')}
          </div>
        </div>
      )}
    </div>
  );
}
